/**
 * StatusBadge — eDMS Design System
 *
 * Renders document FSM state with consistent color + icon + text.
 * Color alone is NEVER used — always paired with text per a11y rules.
 *
 * Source: drafts/31_ux_design_system_spec.md §3.6
 */

import { clsx } from 'clsx';
import styles from './StatusBadge.module.css';

export type DocumentState =
  | 'DRAFT'
  | 'IN_REVIEW'
  | 'REVIEW_REJECTED'
  | 'PENDING_QA_APPROVAL'
  | 'APPROVED_PENDING_EFFECTIVE'
  | 'EFFECTIVE'
  | 'PERIODIC_REVIEW_DUE'
  | 'OBSOLETE'
  | 'WITHDRAWN'
  | 'CANCELLED';

const STATE_CONFIG: Record<DocumentState, { label: string; icon: string }> = {
  DRAFT: { label: 'Draft', icon: '✎' },
  IN_REVIEW: { label: 'In Review', icon: '👁' },
  REVIEW_REJECTED: { label: 'Review Rejected', icon: '↩' },
  PENDING_QA_APPROVAL: { label: 'Pending QA', icon: '⏳' },
  APPROVED_PENDING_EFFECTIVE: { label: 'Approved (Pending Effective)', icon: '⌛' },
  EFFECTIVE: { label: 'Effective', icon: '✓' },
  PERIODIC_REVIEW_DUE: { label: 'Periodic Review Due', icon: '⚠' },
  OBSOLETE: { label: 'Obsolete', icon: '⌫' },
  WITHDRAWN: { label: 'Withdrawn', icon: '✕' },
  CANCELLED: { label: 'Cancelled', icon: '✕' },
};

export interface StatusBadgeProps {
  state: DocumentState;
  /** Compact mode shows icon only (with aria-label). */
  compact?: boolean;
  className?: string;
}

export function StatusBadge({ state, compact, className }: StatusBadgeProps) {
  // Fallback for non-document state strings (training task states,
  // workflow states from other modules, etc.). Renders a generic dot +
  // the raw state token rather than crashing.
  const config = STATE_CONFIG[state] ?? {
    label: String(state ?? 'UNKNOWN').replace(/_/g, ' '),
    icon: '•',
  };
  const safeState = String(state ?? 'unknown').toLowerCase().replace(/_/g, '-');
  return (
    <span
      className={clsx(styles.badge, styles[safeState], className)}
      aria-label={compact ? config.label : undefined}
    >
      <span aria-hidden="true" className={styles.icon}>
        {config.icon}
      </span>
      {!compact && <span className={styles.label}>{config.label}</span>}
    </span>
  );
}
